import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { Episode, MediaItem } from '../types';

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 2000;

/**
 * Edit an episode's title, description and cover.
 *
 * Automatic organization is only the starting point: whatever is saved here
 * is kept through every timeline rebuild. "Use automatic …" brings back the
 * latest generated value. Nothing is sent until Save; Cancel or Escape
 * discards everything.
 */
export default function EditEpisodeDialog({
  episode,
  items,
  onClose,
  onSaved,
  onPhotosChanged,
}: {
  episode: Episode;
  items: MediaItem[];
  onClose: () => void;
  onSaved: (updated: Episode) => void;
  /** Called after a photo leaves the episode, so the page reloads its list. */
  onPhotosChanged: () => Promise<void>;
}) {
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const shownItems = items.filter((i) => !removedIds.includes(i.id));

  const removePhoto = async (id: string) => {
    setRemoving(true);
    setPhotoError(null);
    try {
      await api.removeEpisodeMedia(episode.id, id);
      setRemovedIds((r) => [...r, id]);
      setPendingRemoval(null);
      await onPhotosChanged();
    } catch (err) {
      setPhotoError((err as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(episode.title);
  const [description, setDescription] = useState(episode.description ?? '');
  // 'auto' = let Memoryflix choose; otherwise a media id.
  const [cover, setCover] = useState<string>(episode.coverLocked && episode.coverMediaId ? episode.coverMediaId : 'auto');
  const [restoreTitle, setRestoreTitle] = useState(false);
  const [restoreDescription, setRestoreDescription] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    // showModal() moves focus itself, so place it on the title afterwards.
    titleRef.current?.focus();
    return () => dialog?.close();
  }, []);

  const trimmedTitle = title.replace(/\s+/g, ' ').trim();
  const titleError = !trimmedTitle
    ? 'Give this story a title.'
    : trimmedTitle.length > TITLE_MAX
      ? `Keep the title to ${TITLE_MAX} characters.`
      : null;
  const descriptionError =
    description.trim().length > DESCRIPTION_MAX ? `Keep the description to ${DESCRIPTION_MAX} characters.` : null;
  const coverChoices = items.filter((i) => i.thumbnailUrl || i.posterUrl);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (titleError || descriptionError) return;
    setSaving(true);
    setError(null);
    try {
      const restore: Array<'title' | 'description' | 'cover'> = [];
      const patch: { title?: string; description?: string; coverMediaId?: string } = {};

      if (restoreTitle && trimmedTitle === episode.generatedTitle) restore.push('title');
      else if (trimmedTitle !== episode.title) patch.title = trimmedTitle;

      if (restoreDescription && description === (episode.generatedDescription ?? '')) restore.push('description');
      else if (description.trim() !== (episode.description ?? '').trim()) patch.description = description;

      if (cover === 'auto') {
        if (episode.coverLocked) restore.push('cover');
      } else if (!episode.coverLocked || cover !== episode.coverMediaId) {
        patch.coverMediaId = cover;
      }

      let updated = episode;
      if (restore.length > 0) updated = await api.restoreEpisode(episode.id, restore);
      if (Object.keys(patch).length > 0) updated = await api.updateEpisode(episode.id, patch);
      onSaved(updated);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="edit-episode-title"
      onCancel={(e) => {
        e.preventDefault(); // Escape: close through React state
        onClose();
      }}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose(); // click on the backdrop
      }}
      className="m-auto w-[min(640px,calc(100%-2rem))] rounded-2xl bg-ink-900 p-0 text-white ring-1 ring-white/10 backdrop:bg-black/75 backdrop:backdrop-blur-sm max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none"
    >
      <form onSubmit={save} className="flex max-h-[88vh] flex-col">
        <div className="overflow-y-auto px-5 pt-5 pb-2 sm:px-7 sm:pt-7">
          <h2 id="edit-episode-title" className="mb-1 text-xl font-bold tracking-tight">
            Edit story details
          </h2>
          <p className="mb-6 text-sm text-white/50">Your changes stay put when Memoryflix reorganizes your library.</p>

          {/* ---- Title ---- */}
          <div className="mb-6">
            <div className="mb-1.5 flex items-baseline justify-between">
              <label htmlFor="episode-title" className="text-sm font-medium text-white/80">
                Title
              </label>
              <span className={`text-xs ${trimmedTitle.length > TITLE_MAX ? 'text-red-300' : 'text-white/30'}`}>
                {trimmedTitle.length}/{TITLE_MAX}
              </span>
            </div>
            <input
              id="episode-title"
              ref={titleRef}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setRestoreTitle(false);
              }}
              aria-invalid={Boolean(titleError)}
              aria-describedby="episode-title-help"
              className="focus-ring w-full rounded-md border border-white/15 bg-black/40 px-3 py-2.5 text-base text-white"
            />
            <div id="episode-title-help" className="mt-1.5 text-xs">
              {titleError ? (
                <p className="text-red-300">{titleError}</p>
              ) : (
                episode.generatedTitle &&
                trimmedTitle !== episode.generatedTitle && (
                  <p className="text-white/40">
                    Automatic title: {episode.generatedTitle}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setTitle(episode.generatedTitle ?? '');
                        setRestoreTitle(true);
                      }}
                      className="focus-ring rounded font-semibold text-white/70 underline decoration-white/25 underline-offset-4 hover:text-white"
                    >
                      Use automatic title
                    </button>
                  </p>
                )
              )}
            </div>
          </div>

          {/* ---- Description ---- */}
          <div className="mb-6">
            <label htmlFor="episode-description" className="mb-1.5 block text-sm font-medium text-white/80">
              Description
            </label>
            <textarea
              id="episode-description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setRestoreDescription(false);
              }}
              rows={4}
              placeholder="What happened, who was there, what you remember"
              aria-invalid={Boolean(descriptionError)}
              className="focus-ring w-full resize-y rounded-md border border-white/15 bg-black/40 px-3 py-2.5 text-sm leading-relaxed text-white placeholder:text-white/25"
            />
            <div className="mt-1.5 text-xs">
              {descriptionError ? (
                <p className="text-red-300">{descriptionError}</p>
              ) : (
                episode.descriptionLocked &&
                !restoreDescription && (
                  <button
                    type="button"
                    onClick={() => {
                      setDescription(episode.generatedDescription ?? '');
                      setRestoreDescription(true);
                    }}
                    className="focus-ring rounded font-semibold text-white/60 underline decoration-white/25 underline-offset-4 hover:text-white"
                  >
                    Use automatic description
                  </button>
                )
              )}
            </div>
          </div>

          {/* ---- Photos in this story ---- */}
          <fieldset className="mb-6">
            <legend className="mb-1 text-sm font-medium text-white/80">Photos in this story</legend>
            <p className="mb-3 text-xs text-white/40">
              Take a photo out and it goes back to your library. Nothing is deleted.
            </p>
            <ul className="grid grid-cols-4 gap-2 sm:grid-cols-5">
              {shownItems.map((item) => {
                const thumb = item.type === 'VIDEO' ? item.posterUrl : item.thumbnailUrl ?? item.url;
                const confirming = pendingRemoval === item.id;
                return (
                  <li key={item.id} className="relative">
                    {thumb ? (
                      <img src={thumb} alt="" loading="lazy" className="aspect-square w-full rounded-md object-cover" />
                    ) : (
                      <div className="aspect-square w-full rounded-md bg-white/10" />
                    )}
                    {confirming ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-md bg-black/80 p-1 text-center">
                        <button
                          type="button"
                          disabled={removing || shownItems.length <= 1}
                          onClick={() => void removePhoto(item.id)}
                          className="focus-ring w-full rounded bg-red-500/90 px-1 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                        >
                          Take out
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingRemoval(null)}
                          className="focus-ring text-[11px] text-white/70 underline"
                        >
                          Keep
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={removing || shownItems.length <= 1}
                        onClick={() => setPendingRemoval(item.id)}
                        aria-label="Take this photo out of the story"
                        title={shownItems.length <= 1 ? 'A story needs at least one memory' : 'Take out of the story'}
                        className="focus-ring absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-sm text-white ring-1 ring-white/20 hover:bg-red-600 disabled:opacity-40"
                      >
                        ×
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {photoError && <p className="mt-2 text-xs text-red-300">{photoError}</p>}
          </fieldset>

          {/* ---- Cover ---- */}
          <fieldset className="mb-2">
            <legend className="mb-2 text-sm font-medium text-white/80">Cover image</legend>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              <label
                className={`flex aspect-video cursor-pointer items-center justify-center rounded-md p-2 text-center text-xs ring-2 transition has-[:focus-visible]:ring-accent ${
                  cover === 'auto' ? 'bg-white/10 text-white ring-white' : 'bg-ink-800 text-white/55 ring-transparent hover:ring-white/30'
                }`}
              >
                <input
                  type="radio"
                  name="cover"
                  value="auto"
                  checked={cover === 'auto'}
                  onChange={() => setCover('auto')}
                  className="sr-only"
                />
                Let Memoryflix choose
              </label>
              {coverChoices.map((m, i) => (
                <label
                  key={m.id}
                  className={`relative aspect-video cursor-pointer overflow-hidden rounded-md ring-2 transition has-[:focus-visible]:ring-accent ${
                    cover === m.id ? 'ring-white' : 'ring-transparent hover:ring-white/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="cover"
                    value={m.id}
                    checked={cover === m.id}
                    onChange={() => setCover(m.id)}
                    className="sr-only"
                    aria-label={`Memory ${i + 1}: ${m.originalFilename}`}
                  />
                  <img
                    src={m.thumbnailUrl ?? m.posterUrl ?? ''}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  {m.type === 'VIDEO' && (
                    <span className="absolute right-1 bottom-1 rounded bg-black/70 px-1 text-[10px]">Video</span>
                  )}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {error && (
          <p className="mx-5 mt-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100 sm:mx-7" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3 border-t border-white/5 px-5 py-4 sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="focus-ring rounded-md bg-white/10 px-5 py-2.5 text-sm font-semibold transition hover:bg-white/20"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || Boolean(titleError || descriptionError)}
            className="focus-ring rounded-md bg-white px-6 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
